import { useState } from 'react';
import './Contact.css';
import useReveal from './useReveal';
import { CALENDLY_URL } from '../config';

const FALLBACK_EMAIL = 'anjum@wayfwdstrategies.com';

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const [values, setValues] = useState({ name: '', company: '', email: '', phone: '', message: '' });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const messageRef = useReveal();
  const formRef = useReveal();

  const set = (k) => (e) => {
    if (sent) setSent(false);
    setValues((v) => ({ ...v, [k]: e.target.value }));
  };

  const validate = () => {
    const next = {};
    if (!values.name.trim()) next.name = 'Please enter your name.';
    if (!values.email.trim()) next.email = 'Please enter your email.';
    else if (!emailRe.test(values.email.trim())) next.email = 'Please enter a valid email address.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const subject = `Website enquiry from ${values.name.trim()}`;
    const body = [
      `Name: ${values.name.trim()}`,
      `Company / institution: ${values.company.trim()}`,
      `Email: ${values.email.trim()}`,
      `Phone: ${values.phone.trim()}`,
      '',
      'Message:',
      values.message.trim(),
    ].join('\n');
    const gmailUrl =
      'https://mail.google.com/mail/?view=cm&fs=1'
      + `&to=${encodeURIComponent(FALLBACK_EMAIL)}`
      + `&su=${encodeURIComponent(subject)}`
      + `&body=${encodeURIComponent(body)}`;
    window.open(gmailUrl, '_blank', 'noopener,noreferrer');
    setSent(true);
  };

  return (
    <section id="contact" className="contact">
      <div className="container contact__inner">
        <div className="contact__message reveal--left" ref={messageRef}>
          <h2 className="contact__title">
            Ready to build confident leaders and teams that perform at their best?
          </h2>
          <p className="contact__body">
            Partner with WAYFWD Strategies to build confident professionals, effective leaders and teams that perform at their best across your organisation.
          </p>
          <p className="contact__tagline">
            Helping professionals master the human skills that accelerate career growth.
          </p>
          <div className="contact__lines">
            <a href="tel:+919152128293" className="contact__line">+91 9152128293</a>
            <a href={`mailto:${FALLBACK_EMAIL}`} className="contact__line">{FALLBACK_EMAIL}</a>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="contact__line"
            >
              Book a 30-minute complimentary call
            </a>
          </div>
        </div>

        <div className="contact__form-wrap reveal--right" ref={formRef}>
            <form className="contact__form" onSubmit={onSubmit} noValidate>
              <div className="contact__row">
                <div className="field">
                  <label htmlFor="c-name" className="field__label">Full name<span aria-hidden="true"> *</span></label>
                  <input
                    id="c-name"
                    name="name"
                    type="text"
                    required
                    value={values.name}
                    onChange={set('name')}
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? 'e-name' : undefined}
                    autoComplete="name"
                  />
                  {errors.name && <span id="e-name" className="field__error">{errors.name}</span>}
                </div>

                <div className="field">
                  <label htmlFor="c-company" className="field__label">Company / institution</label>
                  <input
                    id="c-company"
                    name="company"
                    type="text"
                    value={values.company}
                    onChange={set('company')}
                    autoComplete="organization"
                  />
                </div>
              </div>

              <div className="contact__row">
                <div className="field">
                  <label htmlFor="c-email" className="field__label">Email<span aria-hidden="true"> *</span></label>
                  <input
                    id="c-email"
                    name="email"
                    type="email"
                    required
                    value={values.email}
                    onChange={set('email')}
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? 'e-email' : undefined}
                    autoComplete="email"
                  />
                  {errors.email && <span id="e-email" className="field__error">{errors.email}</span>}
                </div>

                <div className="field">
                  <label htmlFor="c-phone" className="field__label">Phone</label>
                  <input
                    id="c-phone"
                    name="phone"
                    type="tel"
                    value={values.phone}
                    onChange={set('phone')}
                    autoComplete="tel"
                  />
                </div>
              </div>

              <div className="field">
                <label htmlFor="c-message" className="field__label">Message</label>
                <textarea
                  id="c-message"
                  name="message"
                  rows={3}
                  value={values.message}
                  onChange={set('message')}
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary contact__submit"
              >
                Contact Us
              </button>

              {sent && (
                <p className="contact__success" role="status" aria-live="polite">
                  Gmail should open in a new tab with your message ready to send. If it doesn't,
                  please email us directly at{' '}
                  <a href={`mailto:${FALLBACK_EMAIL}`}>{FALLBACK_EMAIL}</a>.
                </p>
              )}
            </form>
        </div>
      </div>
    </section>
  );
}
